import clsx from "clsx";
import React from "react";
import { Box, BoxProps, Typography, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { SendUsMessageLink } from "./SendUsMessageLink";
import { useNavigate } from "react-router-dom";
import {
  CLIENT_HOME_ROUTE,
  COACH_PROGRAMS_PUBLISHED_ROUTE,
  HOME_ROUTE,
} from "../../routes/routes";
import { AppLogo } from "./AppLogo";
import useAuth from "../../hooks/auth/useAuth";
import { UserRole } from "../../constants";
import {
  useCurrentBrand,
  useCurrentWorkspace,
} from "../../hooks/useCurrentWorkspace";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContemt: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  logo: {
    marginBottom: theme.spacing(6),
  },
  logoInvert: {
    ...(theme.palette.mode === "dark" && {
      filter: "invert(1)",
    }),
  },
  notFoundTitle: {
    fontSize: 32,
    fontWeight: "normal",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(6),
  },
  button: {
    fontSize: 13,
    fontWeight: "bold",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.125, 4),
    margin: theme.spacing(1),

    borderColor: theme.palette.divider,
    borderWidth: 2,
    borderStyle: "solid",
    minWidth: theme.spacing(20),
  },
  split: {
    border: 0,
    borderTop: `1px solid ${theme.palette.border.primary}`,
    margin: theme.spacing(5, 0, 3),
  },
  notFoundText: {
    fontSize: 16,
    color: theme.palette.text.primary,
  },
}));

export interface ErrorOtherProps extends Omit<BoxProps, "title"> {
  title: React.ReactNode;
  message: React.ReactNode;
  onReset?: () => void;
}

export function ErrorOther(props: ErrorOtherProps) {
  const { className, title, message, onReset, ...other } = props;
  const navigate = useNavigate();
  const s = useStyles();

  // Get role from `useAuth` since current user context is not available on this level
  const { userRole } = useAuth();
  const workspace = useCurrentWorkspace();
  const { isBrandedApp } = useCurrentBrand();

  const isClient = userRole === UserRole.CLIENT;
  const isStridistLogo = !isBrandedApp && !workspace?.logoUrl;
  const homeHref = isClient ? HOME_ROUTE : COACH_PROGRAMS_PUBLISHED_ROUTE;

  const handleBackClick = React.useCallback(() => {
    navigate(homeHref);

    setTimeout(() => onReset && onReset(), 25);
  }, [homeHref, onReset]);

  return (
    <Box className={clsx(s.root, className)} {...other}>
      <AppLogo className={clsx(s.logo, isStridistLogo && s.logoInvert)} />
      <Typography
        className={s.notFoundTitle}
        variant="h2"
        children={
          <>
            {title}
            <br />
            {message}
          </>
        }
      />

      <Box>
        <Button
          className={s.button}
          variant="outlined"
          href={homeHref}
          children="Home"
        />
        <Button
          className={s.button}
          variant="contained"
          onClick={handleBackClick}
          children="Back"
        />
      </Box>

      <hr className={s.split} />
      <Typography className={s.notFoundText}>
        Need help? <SendUsMessageLink />
      </Typography>
    </Box>
  );
}
